import { useState, useCallback, useRef, useEffect } from 'react'
import { useAISuggestionsForNode, useAINodeState } from '../../hooks/useAINodeState'
import type { NodeChatMessage } from './types'
import type { AISuggestion } from '../../types/aiTypes'

interface UseChatSuggestionSyncReturn {
  getSuggestionsForIds: (ids: string[]) => AISuggestion[]
  externalSuggestions: AISuggestion[]
  isExternalGenerating: boolean
}

/** Collect suggestion IDs already referenced in chat messages */
function getAlreadyAnnouncedIds(messages: NodeChatMessage[]): Set<string> {
  const ids = new Set<string>()
  for (const msg of messages) {
    if (msg.suggestionIds) {
      for (const id of msg.suggestionIds) {
        ids.add(id)
      }
    }
  }
  return ids
}

/**
 * Tracks suggestions for a node — only external (sidebar/proactive) generation.
 *
 * Chat-triggered generation is now handled server-side in the tool loop,
 * so this hook no longer needs pendingMessageId tracking or snapshots.
 * It subscribes to the same AIStateStore as the sidebar — no separate data path.
 */
export function useChatSuggestionSync(
  nodeId: string,
  chatMessages: NodeChatMessage[]
): UseChatSuggestionSyncReturn {
  const suggestions = useAISuggestionsForNode(nodeId)
  const aiNodeState = useAINodeState(nodeId)
  const [externalSuggestions, setExternalSuggestions] = useState<AISuggestion[]>([])

  const knownIdsRef = useRef<Set<string> | null>(null)

  // Seed known IDs from chat history on first render only
  if (knownIdsRef.current === null) {
    knownIdsRef.current = getAlreadyAnnouncedIds(chatMessages)
  }

  // Keep knownIdsRef in sync with suggestion IDs from chat messages.
  // This prevents server-side generated suggestions (which are added to the store
  // by useNodeChat and already have a message with suggestionIds) from being
  // re-detected as external suggestions.
  useEffect(() => {
    const known = knownIdsRef.current
    if (!known) return
    for (const msg of chatMessages) {
      if (msg.suggestionIds) {
        for (const id of msg.suggestionIds) {
          known.add(id)
        }
      }
    }
  }, [chatMessages])

  // Detect new external suggestions (from sidebar/proactive generation)
  useEffect(() => {
    const known = knownIdsRef.current!

    const newSuggestions = suggestions.filter((s) => !known.has(s.id))
    if (newSuggestions.length === 0) return

    // Still generating — wait for completion
    if (aiNodeState.isGenerating) return

    // Mark as known immediately to prevent double-detection
    for (const s of newSuggestions) {
      known.add(s.id)
    }

    // Surface as external suggestions
    setExternalSuggestions(newSuggestions)
  }, [suggestions, aiNodeState.isGenerating])

  // Keep external suggestions in sync (remove accepted ones)
  useEffect(() => {
    if (externalSuggestions.length === 0) return
    const currentIds = new Set(suggestions.map((s) => s.id))
    const stillValid = externalSuggestions.filter((s) => currentIds.has(s.id))
    if (stillValid.length !== externalSuggestions.length) {
      setExternalSuggestions(stillValid)
    }
  }, [suggestions, externalSuggestions])

  const getSuggestionsForIds = useCallback(
    (ids: string[]): AISuggestion[] => {
      return suggestions.filter((s) => ids.includes(s.id))
    },
    [suggestions]
  )

  const isExternalGenerating = aiNodeState.isGenerating

  return {
    getSuggestionsForIds,
    externalSuggestions,
    isExternalGenerating,
  }
}
